import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import { Logtail } from '@logtail/browser';
import i18n from './translations';
import { initGoogleTag } from './utils/adsTracking';
import { captureReferral, captureSource } from './utils/sourceTracking';
import { clearSessionForWixEntry, isWixEntry, redirectToLogin } from './utils/session';
import { clearRole } from './utils/permissions';
import { installLocalDiagnostics, localDiagnostic } from './utils/localDiagnostics';
import { isModalSilent } from './utils/apiErrorModal';

installLocalDiagnostics();

// The Wix dashboard entry signs in fresh through /auth/wix/resolve (App.js); an older session in this
// iframe's storage must not send the first requests (see utils/session.js).
clearSessionForWixEntry(window.location, clearRole);

// ── Google Ads tag (conversion measurement) ──
// No-op unless REACT_APP_AW_ID is set; see src/utils/adsTracking.js.
// NEVER on the Webflow OAuth callback: /webflow-register lands with
// ?code=&state= and no third party may observe that URL (Webflow
// Marketplace #1180349). Same guard as the GTM/Reddit snippets in
// public/index.html. No conversion fires from that page anyway.
if (!window.__UB_NO_TELEMETRY__ && process.env.REACT_APP_WIX_LOCAL_PROFILE !== 'isolated' && !window.location.pathname.startsWith('/wix/') && !window.location.pathname.startsWith('/webflow-register')) {
  initGoogleTag();
  // Where the visitor came from (first-party ub_src cookie, never sent to a third party); same guard.
  captureSource();
  // A partner link (?ref=, referral program): first-party ub_ref cookie and one click report; same guard.
  captureReferral();
}

// ── Logtail (Betterstack) browser logger ──
const logtail = !window.__UB_NO_TELEMETRY__ && process.env.REACT_APP_WIX_LOCAL_PROFILE !== 'isolated' && !window.location.pathname.startsWith('/wix/') && process.env.REACT_APP_LOGTAIL_SOURCE_TOKEN
  ? new Logtail(process.env.REACT_APP_LOGTAIL_SOURCE_TOKEN)
  : null;
// The SDK adds the full location.href to every entry; query strings and
// fragments can carry codes and tokens, so only origin + path leave the page.
if (logtail) {
  logtail.use(async (log) => ({ ...log, url: `${window.location.origin}${window.location.pathname}` }));
}

// Override console.error so every error() call is also shipped to Betterstack
const _consoleError = console.error;
let _insideLogtail = false;
console.error = (...args) => {
  _consoleError.apply(console, args);
  localDiagnostic('console_error',{message:args.map(a => a instanceof Error ? a.message : typeof a === 'string' ? a : '[details omitted]').join(' ')});
  if (logtail && !_insideLogtail) {
    _insideLogtail = true;
    logtail.error(args.map(a => {
      if (a instanceof Error) return `${a.message}\n${a.stack}`;
      if (typeof a === 'object') return JSON.stringify(a);
      return String(a);
    }).join(' '));
    _insideLogtail = false;
  }
};

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications." warning that Chrome surfaces (and that CRA's dev overlay
// promotes to a runtime error). It is fired by libraries like MUI when a
// ResizeObserver callback triggers another layout pass — harmless, but it
// pops the red overlay in development. We swallow it before React's
// errorOverlay listener sees it.
const RESIZE_OBSERVER_ERR_RE = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener('error', (e) => {
  if (e?.message && RESIZE_OBSERVER_ERR_RE.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  const msg = e?.reason?.message || String(e?.reason || '');
  if (RESIZE_OBSERVER_ERR_RE.test(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

// Catch uncaught exceptions
window.onerror = (message, source, lineno, colno, error) => {
  if (typeof message === 'string' && RESIZE_OBSERVER_ERR_RE.test(message)) {
    return true; // signal "handled" so the dev overlay ignores it
  }
  if (logtail) {
    logtail.error('Uncaught exception', { message, source, lineno, colno, stack: error?.stack });
  }
};

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (logtail) {
    logtail.error('Unhandled promise rejection', {
      reason: event.reason instanceof Error
        ? { message: event.reason.message, stack: event.reason.stack }
        : String(event.reason),
    });
  }
});

// Log API errors (fired by the fetch interceptor below)
window.addEventListener('api-error', (event) => {
  if (logtail) {
    logtail.warn('API error', { status: event.detail.status, message: event.detail.message });
  }
});

// Global fetch interceptor - auto-adds auth token & handles 401 redirects
const originalFetch = window.fetch;
window._originalFetch = originalFetch; // expose for modules that need raw fetch (e.g. Azure SAS uploads)
window.fetch = async (url, options = {}) => {
  // Check if this is an external URL (not our API)
  const urlString = url.toString();
  const isExternal = urlString.startsWith('http') &&
    !urlString.includes('localhost') &&
    !urlString.includes('127.0.0.1') &&
    !urlString.includes(process.env.REACT_APP_API_BASE_URL || '');

  // Public endpoints that don't need authentication
  const publicPaths = ['/login', '/register', '/password-reset', '/chatbot_response', '/voice_message', '/webhook', '/health', '/recaptcha', '/interaction', '/leads/pre_chat', '/subscription_plans'];
  const isPublic = publicPaths.some(path => urlString.includes(path));

  // Add auth header for non-public, non-external requests if token exists
  if (!isPublic && !isExternal && localStorage.getItem("token")) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${localStorage.getItem("token")}`
    };
  }

  // ngrok's free tier serves an HTML interstitial (ERR_NGROK_6024) to requests
  // with a browser User-Agent, which strips CORS headers and breaks every XHR.
  // This header bypasses it. Gated to ngrok URLs so production is unaffected
  // (no extra preflight on prod requests).
  if (urlString.includes('ngrok')) {
    options.headers = {
      ...options.headers,
      'ngrok-skip-browser-warning': 'true'
    };
  }

  let response;
  try {
    response = await originalFetch(url, options);
  } catch (err) {
    // Network / CORS failures land here (fetch throws TypeError)
    if (err?.name === 'AbortError') throw err;
    localDiagnostic('network_error',{source:urlString,message:err.message});
    if (logtail) {
      logtail.error(`Fetch network error: ${(options.method || 'GET').toUpperCase()} ${urlString} — ${err.message}`, {
        method: (options.method || 'GET').toUpperCase(),
        url: urlString,
        error: err.message,
        stack: err.stack,
        origin: window.location.origin,
        // Path only, never the full href: query strings can carry OAuth
        // callback parameters (?code=&state= on /webflow-register) and must
        // not reach the log service (Webflow Marketplace #1180349).
        page: window.location.pathname,
      });
    }
    throw err; // re-throw so calling code still sees the error
  }

  if(response.status>=400) localDiagnostic('http_error',{source:urlString,status:response.status,
    request_id:response.headers.get('x-local-request-id') || ''});
  // Handle 401 - wipe the session and redirect to login with a message. The
  // redirect carries the current page as ?returnTo= so an expired session
  // (or a revoked one after a password change) drops the user back where
  // they were — e.g. the exact live conversation a notification linked to.
  // An installation grant can expire independently of the account session.
  // Keep Wix entry errors in its own branded recovery screen.
  const isWixInstallation = urlString.startsWith(`${process.env.REACT_APP_API_BASE_URL}wix/apps/`);
  const isWixConnection = ['connect-intent', 'connect-complete'].some(path =>
    urlString.startsWith(`${process.env.REACT_APP_API_BASE_URL}wix/${path}`));
  const isMcpPortal = urlString.startsWith(`${process.env.REACT_APP_API_BASE_URL}mcp/`) ||
    (['/mcp/authorize', '/mcp-connections'].includes(window.location.pathname) &&
     urlString === `${process.env.REACT_APP_API_BASE_URL}auth/validate_token`);
  // The Wix entry pages sign in on their own and never show the portal login (App.js resolveWixInstance).
  if (response.status === 401 && !isPublic && !isWixInstallation && !isWixEntry(window.location)) {
    redirectToLogin({ message: i18n.t("session_expired_message"), clearRole });
  }

  // 403 → unified permission-denied modal (single source of truth).
  // We ONLY surface the modal for user-initiated write attempts
  // (POST/PUT/PATCH/DELETE). Background GET requests that 403 must stay
  // silent — otherwise simply opening a page can spuriously pop the
  // "read-only access" modal when one of many parallel reads hits a
  // permission edge-case (org-context race, stale cache, etc.). The
  // affected component still receives a non-ok response and can handle
  // it locally.
  if (!isPublic && !isExternal && !isWixInstallation && !isWixConnection && !isMcpPortal && response.status === 403) {
    const method = (options.method || 'GET').toUpperCase();
    const isWrite = method !== 'GET' && method !== 'HEAD';
    if (isWrite) {
      let detail = "";
      let status = "";
      try {
        const data = await response.clone().json();
        detail = (data && data.detail) || "";
        status = (data && data.status) || "";
      } catch (_) {}
      // A revoked Webflow authorization is not a permission problem; the
      // Webflow pages report it inline ("reconnect Webflow").
      if (status !== "token_revoked") {
        window.dispatchEvent(
          new CustomEvent("ub:permission_denied", { detail: { message: detail } })
        );
      }
    }
  }

  // The knowledge update check renders its own outcomes inside its dialog: a 429
  // becomes "try again in a moment", a 400 becomes "we could not work out which
  // site to check". Neither is an API error, so neither may pop the generic modal
  // on top of the dialog or log as a warning.
  const isCallerHandled = isMcpPortal ||
    ((response.status === 429 || response.status === 400) &&
    urlString.includes('/knowledge/update_check'));

  // Handle other error responses (404, 429, 500, 502, 503, etc.) — 403
  // is intentionally excluded above so it never produces a duplicate
  // generic "Access Denied" modal alongside the permission modal.
  if (!isPublic && !isExternal && !isCallerHandled && response.status >= 400 && response.status !== 401 && response.status !== 403) {
    let errorMessage = "";

    switch (response.status) {
      case 404:
        errorMessage = i18n.t("api_error_404");
        break;
      case 429:
        errorMessage = i18n.t("api_error_429");
        break;
      case 500:
        errorMessage = i18n.t("api_error_500");
        break;
      case 502:
      case 503:
      case 504:
        errorMessage = i18n.t("api_error_503");
        break;
      default:
        errorMessage = i18n.t("api_error_default");
    }

    // Background-polling endpoints run on a 60s timer for the lifetime of
    // the session, so a single transient 5xx (worker recycle, brief Azure
    // unavailability) reliably spams the user with the generic error modal
    // even though the caller swallows the response. Mark these errors
    // `silent: true` so the modal skips them; the api-error listener above
    // still ships them to Logtail, preserving visibility.
    const SILENT_POLL_PATHS = ['/live/sessions', '/notification_settings'];
    const isSilentPoll = SILENT_POLL_PATHS.some(p => urlString.includes(p));

    // Endpoints whose failures the owning component reports inline (the
    // Inbox renders its own retry state for /chat_sessions; the Webflow OAuth
    // callback shows its own "authorize again" screen for a refused exchange),
    // so the generic modal would double-report on top of an already-handled
    // error. Logtail still receives these via the api-error listener above.
    // Business context also owns its load/save recovery and version-conflict
    // UI, which must keep the user's draft accessible after an HTTP 409.
    // The Inbox asks /live/followup/ for every selected website chat; a chat
    // without a live-chat row (older widgets, chats from before live chat)
    // answers 404 and simply gets no reply box, and a failed reply alerts itself.
    // The Partner program page shows its own messages (e.g. an invalid IBAN), and the partner-link click
    // beacon never bothers the visitor.
    // The build-progress poll (bot?bot_id=) retries on the next tick and the
    // progress screen has its own time limit; every Webflow sign-in step shows
    // its errors inline, the reconnect answers 404 when nothing is parked (the
    // connect then starts the authorization), and the publish prompt and the
    // "publish to finish" card report their own failures (audit 30.10.2026).
    // Every WhatsApp connect (Channels page, agent creation, Wix WhatsApp step) reports its own errors.
    const LOCALLY_HANDLED_PATHS = ['/wix/apps/', '/wix/connect-intent', '/wix/connect-complete', '/auth/instagram/', '/auth/whatsapp/', '/chat_sessions', '/auth/webflow/', '/business_context/', '/management/', '/management-leads/', '/widget_configuration/', '/reporting_settings/', '/notification_settings/', '/agent-studio/', '/apps/bindings', '/live/followup/', '/referral/', '/bonus/', 'bot?bot_id=', '/webflow/publish_pending', '/webflow/publish_site'];
    const isLocallyHandled = LOCALLY_HANDLED_PATHS.some(p => urlString.includes(p));

    // Reads answered with 4xx and repeats of a shown error stay out of the modal too (utils/apiErrorModal.js).
    const silent = isModalSilent({ method: options.method, url: urlString, status: response.status,
                                   listed: isSilentPoll || isLocallyHandled });

    window.dispatchEvent(new CustomEvent('api-error', {
      detail: { status: response.status, message: errorMessage, silent }
    }));
  }

  return response;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <App />
    </LocalizationProvider>
);
